import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, push, set, onValue } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

export default function ChatPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const bottomRef = useRef(null);

  const chatWithName = searchParams.get('chatWithName') || 'User';
  const chatWithPhone = searchParams.get('chatWithPhone') || '';
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  const chatId = user && chatWithPhone
    ? [user.phone, chatWithPhone].filter(Boolean).sort().join('_').replace(/[.#$[\]]/g, '')
    : null;

  useEffect(() => {
    if (!chatId) return;
    const messagesRef = ref(db, 'chats/' + chatId + '/messages');
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map(key => ({ id: key, ...data[key] }));
        list.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        setMessages(list);
      } else {
        setMessages([]);
      }
    });
    return () => unsubscribe();
  }, [chatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
  if (!text.trim() || !chatId) return;
  const messageText = text.trim();
  const messagesRef = ref(db, 'chats/' + chatId + '/messages');
  const newMsgRef = push(messagesRef);
  await set(newMsgRef, {
    text: messageText,
    type: 'text',
    senderPhone: user.phone,
    senderName: user.name,
    timestamp: Date.now(),
    read: false,
  });
  setText('');

  const myIndexPhone = user.phone.replace(/[.#$[\]]/g, '');
  const theirIndexPhone = chatWithPhone.replace(/[.#$[\]]/g, '');

  await set(ref(db, 'userChats/' + myIndexPhone + '/' + chatId), {
    chatId, otherName: chatWithName, otherPhone: chatWithPhone, from, to,
    lastMessage: messageText, lastTimestamp: Date.now(), lastSenderPhone: user.phone,
  });
  await set(ref(db, 'userChats/' + theirIndexPhone + '/' + chatId), {
    chatId, otherName: user.name, otherPhone: user.phone, from, to,
    lastMessage: messageText, lastTimestamp: Date.now(), lastSenderPhone: user.phone,
  });
};

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!user) return null;

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <div style={styles.header}>
          <div style={styles.headerAvatar}>
            {chatWithName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div>
            <div style={styles.headerName}>{chatWithName}</div>
            <div style={styles.headerRoute}>{from} → {to}</div>
          </div>
        </div>

        <div style={styles.messagesArea}>
          {messages.length === 0 ? (
            <p style={styles.emptyText}>No messages yet — say hello!</p>
          ) : (
            messages.map(msg => {
              const isMine = msg.senderPhone === user.phone;
              return (
                <div key={msg.id} style={{ ...styles.bubbleWrap, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                  <div style={isMine ? styles.bubbleMine : styles.bubbleTheirs}>
                    <div>{msg.text}</div>
                    <div style={{ ...styles.bubbleTime, color: isMine ? 'rgba(255,255,255,0.7)' : '#9499b5' }}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef}></div>
        </div>

        <div style={styles.inputRow}>
          <input
            style={styles.textInput}
            placeholder={t('typeMessage')}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button style={styles.sendBtn} onClick={sendMessage}>➤</button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 700, margin: '0 auto', padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)' },
  header: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: '#eaf5fc', borderRadius: 14, padding: '14px 16px', marginBottom: 12,
    border: '1px solid #d9edfa',
  },
  headerAvatar: {
    width: 40, height: 40, borderRadius: 20, background: '#3a8fd0',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
  },
  headerName: { color: '#1a1a2e', fontWeight: 700, fontSize: 15 },
  headerRoute: { color: '#5a5f7d', fontSize: 12 },
  messagesArea: { flex: 1, overflowY: 'auto', padding: '10px 4px', display: 'flex', flexDirection: 'column' },
  emptyText: { textAlign: 'center', color: '#9499b5', fontSize: 13, marginTop: 40 },
  bubbleWrap: { display: 'flex', marginBottom: 8 },
  bubbleMine: { maxWidth: '70%', background: '#3a8fd0', color: '#fff', borderRadius: '14px 14px 4px 14px', padding: '8px 12px', fontSize: 14 },
  bubbleTheirs: { maxWidth: '70%', background: '#f7f9fc', color: '#1a1a2e', borderRadius: '14px 14px 14px 4px', padding: '8px 12px', fontSize: 14, border: '1px solid #eef1f8' },
  bubbleTime: { fontSize: 10, marginTop: 3 },
  inputRow: { display: 'flex', gap: 8, background: '#ffffff', border: '1px solid #eef1f8', borderRadius: 24, padding: '6px 6px 6px 16px' },
  textInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, background: 'transparent' },
  sendBtn: { width: 36, height: 36, borderRadius: 18, background: '#3a8fd0', color: '#fff', border: 'none', fontSize: 15, cursor: 'pointer', flexShrink: 0 },
};